/* eslint-disable no-bitwise */
import { Injectable, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AbstractUtilitiesService } from '@abstract-services/utilities/1.0/utilities.abstract-service';
import { NavigationItem } from '@models/navigation-item/1.0/navigation-item';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
export interface InvalidControl {
  path: string;
  value: any;
}
import { Observable, catchError, defer, from, lastValueFrom, of } from 'rxjs';
import { DateService } from '@services/utilities/1.0/date.service';
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';

@Injectable({
  providedIn: 'root',
})
export class UtilitiesService implements AbstractUtilitiesService {
  constructor(
    private dateService: DateService,
    @Optional() private translateService: TranslateService,
  ) {}

  generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const R = (Math.random() * 16) | 0;
      const V = c === 'x' ? R : (R & 0x3) | 0x8;
      return V.toString(16);
    });
  }

  /**
   * Formats a phone number using brand country code heuristics.
   * - If ANI already starts with '+', returns '+' + digits-only version
   * - Else, if digits already start with the country calling code, prefix '+'
   * - Else, prefix '+' and the country calling code derived from brand country
   */
  formatPhoneNumberWithCountry(ani: string, brandCountryCode: CountryCode): string {
    if (!ani) return '';
    const trimmed = `${ani}`.trim();
    if (trimmed.startsWith('+')) {
      const cleaned = trimmed.replace(/\D/g, '');
      return `+${cleaned}`;
    }

    const digitsOnly = trimmed.replace(/\D/g, '');
    const callingCode = getCountryCallingCode(brandCountryCode).toString();

    if (digitsOnly.startsWith(callingCode)) {
      return `+${digitsOnly}`;
    }

    return `+${callingCode}${digitsOnly}`;
  }

  indexOfUrlGUID(url: string): number {
    // More flexible GUID regex that accepts all UUID versions, not just v4
    const regexpGUID = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/;
    const match = regexpGUID.exec(url);
    return match ? match.index : -1;
  }

  /**
   * Returns a map from the given array with the map keys being the
   * array value of the given mapKey name
   *
   * @param array - array to get map for
   * @param mapKeyProperty - property of array object that should be used as the map key
   */
  convertArrayToMap(array: any[], mapKeyProperty: string): any {
    const map = {};
    if (!Array.isArray(array)) {
      return map;
    }
    for (const value of array) {
      map[value[mapKeyProperty]] = value;
    }
    return map;
  }

  /**
   * Returns a multidimensional map with each value in keyProperties as a sub-key
   * e.g. keyProperties=['a','b'], map = {'a': {'b': item}}
   *
   * @param array - array to convert
   * @param keyProperties - properties to have in map
   */
  convertArrayToMultiDimensionalMap(array: any[], keyProperties: string[]): any {
    const map = {};
    for (const row of array) {
      let nextMap = map;
      const keyLen = keyProperties.length;
      for (let k = 0; k < keyLen - 1; k++) {
        const prop = keyProperties[k];
        if (!nextMap[row[prop]]) {
          nextMap[row[prop]] = {};
        }
        nextMap = nextMap[row[prop]];
      }
      nextMap[row[keyProperties[keyLen - 1]]] = row;
    }
    return map;
  }

  /**
   * Returns a flat list of menus
   *
   * @param navigationItems - list to flatten
   * @return  navList - flat list
   */
  flattenNavigationMenu(navItems: NavigationItem[]): NavigationItem[] {
    const navList = [];
    for (const item of navItems) {
      navList.push(item);
      if (item.children && item.children.length) {
        navList.push(...this.flattenNavigationMenu(item.children));
      }
    }
    return navList;
  }

  /**
   * Returns true if a menu matches a link
   *
   * @param link - the link from the navItem
   * @param url - the url from the browser address
   */
  navMatch(link: string, url: string): RegExpMatchArray {
    // remove the leading .
    link = link.replace('.', '');
    return url.match(link);
  }

  /**
   * Modifies the passed in nav list
   *
   * @param navList - the link from the navItem
   * @param url - the url from the browser address
   */
  updateNavItems(navList: NavigationItem[], url: string) {
    for (const item of navList) {
      if (item.link) {
        if (this.navMatch(item.link, url)) {
          item.selected = true;
        } else {
          item.selected = false;
        }
      }
    }
  }

  /**
   * Modifies original array by removing items that pass the filterFn. Returns an array of the items removed.
   *
   * @param array - array of items to modify
   * @param filterFn - filter function
   */
  removeFromArray(array: any[], filterFn: (T) => boolean): any[] {
    const removedList = [];
    for (let i = 0; i < array.length; ) {
      const item = array[i];
      if (filterFn(item)) {
        removedList.push(item);
        array.splice(i, 1);
      } else {
        i++;
      }
    }
    return removedList;
  }

  /**
   * Convert an UTC Date and time to the given timezone in M/DD/YYYY HH:mm A format
   *
   * @param date - UTC date and time
   * @param tzString - Time zone code string from sre/generated/tcea/models
   */
  convertTZ(date: string | Date, tzString: string, format = 'MM/DD/YYYY HH:mm A'): string {
    if (!date) return '';

    const localDate = new Date(
      (typeof date === 'string' ? new Date(date) : date)?.toLocaleString('en-US', {
        timeZone: tzString,
      }),
    );
    return this.dateService.format(localDate, format);
  }

  /**
   * Gets the difference between two objects' keys and values
   * @param o1 {Object}, @param o2 {Object}
   * @returns object diff
   */
  getObjectDiff(o1: Object, o2: Object): Object {
    return Object.keys(o2).reduce((diff, key) => {
      if (o1[key] === o2[key]) {
        return diff;
      }
      return {
        ...diff,
        [key]: o2[key],
      };
    }, {});
  }

  /**
   * Converts an HTML element to a canvas using html2canvas with lazy loading
   * @param element - The HTML element to capture
   * @param options - Optional configurations for html2canvas
   * @returns A Promise resolving to an HTMLCanvasElement or rejects with an error
   */
  convertHtmlToCanvasWrapper(element: HTMLElement, options?: any): Promise<HTMLCanvasElement> {
    return lastValueFrom(
      from(import('html2canvas')).pipe(
        catchError(() => {
          console.error('Failed to load html2canvas');
          return of({ default: () => Promise.reject('html2canvas failed to load') });
        }),
      ),
    ).then(({ default: html2canvas }) => html2canvas(element, options));
  }

  /**
   * Generates a PDF from an HTML element with lazy-loaded jsPDF.
   * @param element - The HTML element to capture.
   * @param scale - Scaling factor for the canvas.
   * @param quality - JPEG quality (0.1 to 1.0, default 0.8)
   * @returns A Promise resolving to a PDF Blob.
   */
  generatePdf(element: HTMLElement, scale: number = 2, quality: number = 0.8): Promise<Blob> {
    return this.convertHtmlToCanvasWrapper(element, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
    }).then((canvas) => {
      const margin = 20;
      const pageWidth = 450;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Use JPEG with compression instead of PNG
      const contentDataURL = canvas.toDataURL('image/jpeg', quality);

      // Lazy load jsPDF dynamically
      return lastValueFrom(from(import('jspdf'))).then(({ jsPDF }) => {
        const pdf = new jsPDF({ unit: 'px' });
        pdf.addImage(contentDataURL, 'JPEG', margin, margin, imgWidth, imgHeight);
        return pdf.output('blob');
      });
    });
  }

  /**
   * Resizes an image file to an specific width and height
   * @param file
   * @param width
   * @param height
   * @returns
   */
  resizeImage(file: File, width: number, height: number): Observable<File> {
    return new Observable((observer) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          let canvas: HTMLCanvasElement | OffscreenCanvas;
          let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

          if (window.OffscreenCanvas) {
            canvas = new OffscreenCanvas(width, height);
            ctx = (canvas as OffscreenCanvas).getContext('2d');
          } else {
            canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            ctx = (canvas as HTMLCanvasElement).getContext('2d');
          }

          ctx.drawImage(img, 0, 0, width, height);

          const handleBlob = (blob: Blob | null) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              observer.next(resizedFile);
              observer.complete();
            } else {
              observer.error(new Error('Canvas is empty'));
            }
          };
          if (window.OffscreenCanvas) {
            (canvas as OffscreenCanvas)
              .convertToBlob({ type: file.type })
              .then(handleBlob)
              .catch((err) => observer.error(err));
          } else {
            (canvas as HTMLCanvasElement).toBlob(handleBlob, file.type);
          }
        };

        img.onerror = (err) => observer.error(err);
        img.src = event.target.result as string;
      };

      reader.onerror = (error) => observer.error(error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get the dimensions of a file
   * @param file
   * @returns
   */
  getFileDimension(file: File) {
    const promise = new Promise<{ width: number; height: number }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = (err) => reject(err);
        img.src = event.target.result as string;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

    return defer(() => from(promise));
  }

  findInvalidControls(control: AbstractControl, path: string = ''): InvalidControl[] {
    const invalidControls: InvalidControl[] = [];
    const changedControls: InvalidControl[] = [];

    if (control instanceof UntypedFormGroup) {
      Object.keys(control.controls).forEach((key) => {
        const childControl = control.controls[key];
        const currentPath = path ? `${path}.${key}` : key;
        invalidControls.push(...this.findInvalidControls(childControl, currentPath));
      });
    } else if (control instanceof UntypedFormArray) {
      control.controls.forEach((childControl, index) => {
        const currentPath = `${path}[${index}]`;
        invalidControls.push(...this.findInvalidControls(childControl, currentPath));
      });
    } else {
      if (control.invalid) {
        invalidControls.push({ path, value: control.value });
      }
      if (control && !control.pristine) {
        changedControls.push({ path, value: control.value });
      }
    }
    if (invalidControls.length > 0) {
      console.error('invalid controls', invalidControls);
    }
    if (changedControls.length > 0) {
      console.error('changed controls', changedControls);
    }
    return invalidControls && changedControls;
  }

  /**
   * Translate device type enum with fallback to original string
   * @param deviceType - The device type to translate
   * @returns Translated device type or original string if translation doesn't exist
   */
  translateDeviceType(deviceType: string): string {
    if (!deviceType) return '';
    if (!this.translateService) return deviceType;

    const translationKey = `orders.orderInfo.deviceTypes.${deviceType}`;
    const translated = this.translateService.instant(translationKey);
    // If translation key is the same as the result, it means translation doesn't exist
    return translated !== translationKey ? translated : deviceType;
  }

  /**
   * Translate ordering channel enum with fallback to original string
   * @param orderingChannel - The ordering channel to translate
   * @returns Translated ordering channel or original string if translation doesn't exist
   */
  translateOrderingChannel(orderingChannel: string): string {
    if (!orderingChannel) return '';
    if (!this.translateService) return orderingChannel;

    const translationKey = `orders.orderInfo.orderingChannels.${orderingChannel}`;
    const translated = this.translateService.instant(translationKey);
    // If translation key is the same as the result, it means translation doesn't exist
    return translated !== translationKey ? translated : orderingChannel;
  }
}
